import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  activateBusinessApp,
  markBusinessAppReviewed,
  type BusinessAppDetail,
  type BusinessAppSummary,
  type ChecklistItem,
} from '../api/businessApps'
import { toast } from '../ui/notify'
import { appUrl, failed } from './parts'
import '../inventoree/UnassignedBand.css'
import './Lifecycle.css'

/**
 * CICLUL DE VIATA ITIL pe ecran - `v4/api/src/BusinessApps/Completeness.php`.
 *
 * Coordonatoarea si Bogdan, 5 octombrie 2026, "ca la carte": ce gaseste
 * detectia nu mai sta ascuns in Suggested (cele cu incredere mare intra
 * singure in lista, `Pending review`), iar fiecare aplicatie cere datele ca
 * in ITIL - serverele confirmate, service owner, suportul, programul,
 * escaladarea. Checklistul il calculeaza serverul; aici doar se arata:
 *
 *  - `AttentionBand`   banda rosie din capul listei (stilul benzii de statii
 *                      fara utilizator, `UnassignedBand.css`): "N applications
 *                      need completing", chevron, ce lipseste la fiecare;
 *  - `LifecycleBadges` in lista: "Pending review" si semnul rosu cu numarul
 *                      de lipsuri (lista in tooltip);
 *                      "Complete" deschide fereastra de completare rapida
 *                      (`QuickComplete.tsx`) pentru cine poate edita; insigna
 *                      rosie din bara (`AppsAttentionMark`) o deschide;
 *  - `CompletenessCard` pe fisa: checklistul cu legaturi la sectiunea de
 *                      completat, "Confirm & activate", "Mark reviewed".
 *                      Coordonatoarea, 6 octombrie 2026: cand nu lipseste
 *                      nimic obligatoriu (recomandarile nu conteaza) cardul
 *                      NU mai apare; revizia anuala depasita e o lipsa, deci
 *                      il aduce inapoi. Data urmatoarei revizii trece in
 *                      antet, discret (`NextReviewNote`).
 */

/** Ancora sectiunii de pe fisa unde se completeaza randul. */
export const SECTION_ANCHOR: Record<ChecklistItem['section'], string> = {
  components: 'app-sec-components',
  details: 'app-sec-details',
  support: 'app-sec-support',
  completeness: 'app-sec-completeness',
  runbook: 'app-sec-runbook',
}

function missingTitle(app: BusinessAppSummary): string {
  return (app.missing ?? []).map((m) => `- ${m.label}`).join('\n')
}

export function AttentionBand({
  apps,
  openSignal = 0,
  onComplete,
}: {
  apps: BusinessAppSummary[]
  /** Creste cand banda trebuie deschisa si adusa pe ecran (insigna din bara, `?attention=1`). */
  openSignal?: number
  /** Cu drept de editare: "Complete" deschide fereastra de completare rapida (`QuickComplete.tsx`). */
  onComplete?: (id: number) => void
}) {
  const [open, setOpen] = useState(openSignal > 0)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (openSignal < 1) return
    setOpen(true)
    ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [openSignal])
  const list = apps.filter((a) => a.needsAttention === true)
  if (list.length === 0) return null
  const pending = list.filter((a) => a.lifecycle === 'pending_review').length
  const due = list.filter((a) => a.reviewDue === true).length
  const label = list.length === 1 ? '1 application needs completing' : `${list.length} applications need completing`

  return (
    <div className={`od-unknown-band app-attention-band${open ? ' is-open' : ''}`} id="appsAttention" ref={ref}>
      <button type="button" className="od-unknown-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <i className="fas fa-clipboard-check" aria-hidden="true"></i>
        <strong className="od-unknown-count">{label}</strong>
        {pending > 0 ? <span className="od-unknown-last">{pending} found automatically, to review</span> : null}
        {due > 0 ? <span className="od-unknown-last">{due} due for the yearly review</span> : null}
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} od-unknown-chevron`} aria-hidden="true"></i>
      </button>
      {open ? (
        <div className="od-unknown-body">
          <table className="table table-condensed od-unknown-table">
            <thead>
              <tr>
                <th>Application</th>
                <th>Status</th>
                <th>Missing</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.id} data-id={a.id}>
                  <td>
                    <a href={appUrl(a.id)} target="_blank" rel="noopener noreferrer">{a.name}</a>
                  </td>
                  <td>{a.lifecycle === 'pending_review' ? 'Pending review' : a.reviewDue ? 'Review due' : 'Active'}</td>
                  <td>{(a.missing ?? []).map((m) => m.label).join(' · ')}</td>
                  <td className="od-unknown-actions">
                    {onComplete ? (
                      <button type="button" className="btn btn-xs btn-theme app-complete-btn" onClick={() => onComplete(a.id)}>
                        {a.lifecycle === 'pending_review' ? 'Review' : 'Complete'}
                      </button>
                    ) : (
                      <a className="btn btn-xs btn-theme" href={`${appUrl(a.id)}#${SECTION_ANCHOR.completeness}`} target="_blank" rel="noopener noreferrer">
                        {a.lifecycle === 'pending_review' ? 'Review' : 'Complete'}
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}

export function LifecycleBadges({ app }: { app: BusinessAppSummary }) {
  const count = app.missingCount ?? 0
  return (
    <>
      {app.lifecycle === 'pending_review' ? (
        <span className="adm-pill adm-pill-warn app-pending-pill" title="Found automatically - confirm the servers and activate it">
          Pending review
        </span>
      ) : null}
      {count > 0 ? (
        <span className="app-missing-mark" title={`Missing:\n${missingTitle(app)}`} aria-label={`${count} missing: ${(app.missing ?? []).map((m) => m.label).join(', ')}`}>
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {count}
        </span>
      ) : null}
    </>
  )
}

/** Lipseste ceva obligatoriu? Recomandarile (runbook, backup, expirari) nu tin cardul. */
function incomplete(app: BusinessAppSummary & { checklist?: ChecklistItem[] }): boolean {
  if (typeof app.needsAttention === 'boolean') return app.needsAttention
  return (app.checklist ?? []).some((c) => !c.done && c.recommended !== true)
}

/**
 * Data urmatoarei revizii, langa insignele din antetul fisei - numai cand
 * cardul Completeness nu se vede (acolo o spune el).
 */
export function NextReviewNote({ app }: { app: BusinessAppDetail['app'] }) {
  if (!app.checklist || !app.reviewDueAt || app.reviewDue || app.lifecycle === 'pending_review' || incomplete(app)) return null
  return (
    <span className="adm-muted app-next-review" title="All required details are filled in. The yearly review is due on this date.">
      <i className="fas fa-clipboard-check" aria-hidden="true"></i> complete · next review {app.reviewDueAt.slice(0, 10)}
    </span>
  )
}

export function CompletenessCard({ page }: { page: BusinessAppDetail }) {
  const app = page.app
  const query = useQueryClient()
  const [busy, setBusy] = useState(false)
  if (!app.checklist) return null
  const pending = app.lifecycle === 'pending_review'
  if (!pending && !app.reviewDue && !incomplete(app)) return null
  const reload = () => void query.invalidateQueries({ queryKey: ['business-apps'] })

  const act = (call: (id: number) => Promise<{ status: string; message: string }>) => {
    setBusy(true)
    call(app.id)
      .then((result) => {
        toast(result.message, 'Applications', result.status === 'OK' ? 'success' : 'error')
        if (result.status === 'OK') reload()
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  const done = app.checklist.filter((c) => c.done).length

  return (
    <div id={SECTION_ANCHOR.completeness} className="app-completeness-wrap">
      <div className="adm-section">
        <h4>Completeness</h4>
        <span className="adm-section-note">
          {done} of {app.checklist.length} done
          {app.origin === 'discovery' ? ' · found automatically' : ''}
          {app.reviewDueAt ? ` · next review ${app.reviewDueAt.slice(0, 10)}` : ''}
        </span>
        {page.canEdit && pending ? (
          <button
            type="button"
            id="appActivate"
            className="btn btn-success btn-xs"
            style={{ marginLeft: 'auto' }}
            disabled={busy || page.components.length === 0}
            title={page.components.length === 0 ? 'Add at least one server first' : 'The servers below are right - make the application active'}
            onClick={() => act(activateBusinessApp)}
          >
            <i className="fas fa-check"></i> Confirm &amp; activate
          </button>
        ) : null}
        {page.canEdit && app.reviewDue ? (
          <button type="button" id="appReviewed" className="btn btn-primary btn-xs" style={{ marginLeft: 'auto' }} disabled={busy} onClick={() => act(markBusinessAppReviewed)}>
            <i className="fas fa-clipboard-check"></i> Mark reviewed
          </button>
        ) : null}
      </div>
      <div className={`adm-card app-completeness${app.needsAttention ? ' is-incomplete' : ''}`} id="appCompleteness">
        {pending ? (
          <p className="app-completeness-note">
            <i className="fas fa-magic"></i> Found automatically on the customer&apos;s machines. Check the servers under{' '}
            <a href={`#${SECTION_ANCHOR.components}`}>Components</a> - remove the wrong ones - then Confirm &amp; activate.
          </p>
        ) : null}
        {app.reviewDue ? (
          <p className="app-completeness-note is-due">
            <i className="fas fa-history"></i> The yearly review is due: check the servers, the owner and the support details, then Mark reviewed.
          </p>
        ) : null}
        <ul className="app-checklist">
          {app.checklist.map((c) => (
            <li key={c.key} className={c.done ? 'is-done' : c.recommended ? 'is-missing is-recommended' : 'is-missing'} data-key={c.key}>
              <i className={c.done ? 'fas fa-check-circle' : c.recommended ? 'fas fa-exclamation-triangle' : 'far fa-circle'} aria-hidden="true"></i>{' '}
              {c.done ? c.label : <a href={`#${SECTION_ANCHOR[c.section]}`}>{c.label}</a>}
              <span className="sr-only">{c.done ? ' (done)' : c.recommended ? ' (recommended)' : ' (missing)'}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
